import {
  Box,
  Dialog,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {ActionButton} from '../buttons';
import {useState} from 'react';

type ModalProps = {
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  title: string;
  description: React.ReactNode;
  onConfirm: () => void | Promise<void>;
};

export const Modal = ({
  modalOpen,
  setModalOpen,
  title,
  description,
  onConfirm,
}: ModalProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={modalOpen}
      onClose={() => setModalOpen(false)}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          maxWidth: isMobile ? '100%' : 480,
          borderRadius: isMobile ? 0 : 2,
          maxHeight: isMobile ? '100%' : '90vh',
        },
      }}
    >
      <Box sx={{overflow: 'auto', flex: 1}}>
        <Box sx={{px: isMobile ? 2 : 4, pt: isMobile ? 3 : 4, pb: 2}}>
          <Stack spacing={2}>
            <Typography
              variant="h6"
              sx={{
                fontSize: 16,
                fontWeight: 600,
                lineHeight: 1,
                letterSpacing: '0.15px',
                color: theme.palette.text.primary,
              }}
            >
              {title}
            </Typography>
            <Typography
              sx={{
                fontSize: 14,
                color: theme.palette.text.secondary,
                whiteSpace: 'pre-line',
              }}
            >
              {description}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
                paddingTop: 2,
              }}
            >
              <ActionButton
                variant="primary"
                onClick={handleConfirm}
                loading={loading}
                disabled={loading}
              >
                {title}
              </ActionButton>
              <ActionButton
                variant="outline"
                onClick={() => setModalOpen(false)}
                disabled={loading}
                sx={{
                  px: 1.5,
                  py: 0.5,
                }}
              >
                Cancel
              </ActionButton>
            </Box>
          </Stack>
        </Box>
      </Box>
    </Dialog>
  );
};
